import FluidListView from "components/layout/FluidListView";
import { useSelectedRobots } from "../FleetPage";
import { useCallback, useMemo, useState } from "react";
import FunctionalButton from "components/layout/FunctionalButton";
import { Fleet } from "types/fleet";
import { useFilteredMultiARCs } from "providers/MultiARCsControlProvider";
import { useSubpage } from "components/layout/Subpage";
import Section from "components/layout/Section";
import ARCCard from "components/ARC/ARCCard";
import Subpages from "./Subpages";
import { useFleet } from "providers/FleetProvider";
import { useOrgFleet } from "providers/OrgFleetProvider";
import ControlARCDialog from "../components/ControlARCDialog";
import { ARCInfo } from "arcjs/common/arc_devices";

export type ActionBarProps = {
    onAction: (actionName: string) => void;
} & React.HTMLProps<HTMLDivElement>;

const ActionBar = ({ className, onAction, ...props }: ActionBarProps) => {
    const { selectedRobots: selectedARCs } = useSelectedRobots();
    const buttonProps = useMemo(
        () => ({
            className: "button w-fit",
        }),
        []
    );

    const buttons = useMemo(
        () => [
            { title: "Clear", disabled: selectedARCs.length === 0, action: "deselect" },
            {
                title: "Assign mission",
                disabled: selectedARCs.length !== 1,
                action: "mission_assign",
            },
            {
                title: "Navigate To",
                disabled: selectedARCs.length === 0,
                action: "navigate_to",
            },
        ],
        [selectedARCs]
    );

    return (
        <div {...props} className={`flex  gap-2 text-sm ${className} relative`}>
            {buttons.map((b) => (
                <FunctionalButton
                    {...buttonProps}
                    title={b.title}
                    onClick={() => onAction(b.action)}
                    disabled={b.disabled}
                    key={b.action}
                />
            ))}
        </div>
    );
};

const ARCListSection = ({
    className,
    fleet,
}: React.HTMLProps<HTMLDivElement> & { fleet: Fleet }) => {
    const [controlTarget, setControlTarget] = useState<ARCInfo | null>(null);
    const { selectedRobots, robotSelection } = useSelectedRobots();
    const { setSubpageId } = useSubpage();
    const { arcStatuses } = useFleet();
    const { proxyHostname } = useOrgFleet();

    const onAction = useCallback(
        (action: string) => {
            switch (action) {
                case "deselect":
                    robotSelection.clear();
                    break;
                case "mission_assign":
                case "navigate_to":
                    setSubpageId(action);
                    break;
                default:
                    break;
            }
        },
        [robotSelection, setSubpageId]
    );

    const { arcs: arcsInfo } = fleet;
    const { arcs } = useFilteredMultiARCs({ arcSerials: arcsInfo.map((i) => i.serialNumber) });

    return (
        <Section className={className}>
            <Section.Header>ARCs</Section.Header>
            <Section.Content>
                <div className={`flex h-full w-full flex-col pb-1`}>
                    <ActionBar className="grow-0 pb-4" onAction={onAction} />
                    <FluidListView>
                        {arcs.map((arc) => (
                            <ARCCard
                                onClick={(arc) => setControlTarget(arc.info)}
                                action={() => setControlTarget(arc.info)}
                                key={arc.serialNumber}
                                arc={arc}
                                arcStatus={arcStatuses[arc.serialNumber]}
                                className={`${
                                    selectedRobots.includes(arc.serialNumber)
                                        ? "bg-selected-card"
                                        : ""
                                }`}
                            />
                        ))}
                    </FluidListView>
                </div>
            </Section.Content>
            <Subpages className={`absolute inset-0 bg-black90`} />
            <ControlARCDialog
                arc={controlTarget}
                proxyHostname={proxyHostname}
                visible={Boolean(controlTarget)}
                onClose={() => setControlTarget(null)}
            />
        </Section>
    );
};

export default ARCListSection;
